import React, { createContext, useContext, useState, useEffect } from 'react';

export type GetAuthToken = () => Promise<string>;

export interface IConversationHistoryContext {
  resumedConversationId: string;
  setResumedConversationId: (id: string) => void;
  hasConversations: boolean;
  setHasConversations: (value: boolean) => void;
  productName: string | undefined;
  getAuthToken: GetAuthToken | undefined;
}

export interface ConversationHistoryProviderProps {
  children: React.ReactNode;
  productName?: string;
  getAuthToken?: GetAuthToken;
}

const ConversationHistoryContext = createContext<IConversationHistoryContext | undefined>(undefined);

export const ConversationHistoryProvider: React.FC<ConversationHistoryProviderProps> = ({
  children,
  productName,
  getAuthToken,
}: ConversationHistoryProviderProps) => {
  const [resumedConversationId, setResumedConversationId] = useState<string>('');

  const [hasConversations, setHasConversations] = useState<boolean>(() => {
    const stored = localStorage.getItem('hasConversations');
    return stored ? stored === 'true' : false;
  });

  // Persist hasConversations to localStorage when it changes
  useEffect(() => {
    if (hasConversations !== undefined) {
      localStorage.setItem('hasConversations', hasConversations.toString());
    }
  }, [hasConversations]);

  return (
    <ConversationHistoryContext.Provider
      value={{
        resumedConversationId,
        setResumedConversationId,
        hasConversations,
        setHasConversations,
        productName,
        getAuthToken,
      }}
    >
      {children}
    </ConversationHistoryContext.Provider>
  );
};

export const useConversationHistoryContext = () => {
  const context = useContext(ConversationHistoryContext);
  if (context === undefined) {
    throw new Error('useConversationHistoryContext must be used within a ConversationHistoryProvider');
  }
  return context;
};

export const withConversationHistoryProvider = <P extends object & { getAuthToken?: GetAuthToken }>(
  WrappedComponent: React.ComponentType<P>,
  productName?: string
) => (props: P) => (
  <ConversationHistoryProvider productName={productName} getAuthToken={props.getAuthToken}>
    <WrappedComponent {...props} />
  </ConversationHistoryProvider>
  );
